import { useEffect, useState } from 'react';
import type { Notification } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useInfinite } from '../../lib/query';
import { Link, navigate } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { Avatar, Button, Chips, EmptyState, LoadMore, Skeleton, cx } from '../../components/ui/primitives';
import { Page, PageHeader } from '../../components/layout';
import { FollowButton } from '../../components/UserRow';
import { timeAgo } from '../../lib/time';
import styles from './notifications.module.css';

const TEXT: Record<Notification['type'], string> = {
  follow: 'is je gaan volgen',
  video_like: 'vindt je video leuk',
  comment: 'reageerde op je video',
  comment_reply: 'antwoordde op je reactie',
  mention: 'noemde je',
  sound_used: 'gebruikte je geluid',
  playlist_follow: 'volgt je playlist',
};

type Filter = 'all' | 'unread';

export function NotificationsPage() {
  const { me, setMe } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  const list = useInfinite<Notification & { followingActor?: boolean }>(`notifications:${me?.id}:${filter}`, (cursor) => api.page<Notification & { followingActor?: boolean }>('/notifications', { cursor, unread: filter === 'unread' ? 'true' : undefined }));
  const [readLocally, setReadLocally] = useState(false);

  // Opening the inbox marks everything as read (items keep their unread style until next visit).
  useEffect(() => {
    if (!me || me.unreadNotifications === 0) return;
    const t = setTimeout(() => {
      void api.post('/notifications/read', { all: true }).then(() => {
        setMe({ ...me, unreadNotifications: 0 });
        setReadLocally(true);
      });
    }, 1500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (n: Notification) => {
    if (n.type === 'follow') return navigate(`/u/${n.actor.username}`);
    if (n.type === 'playlist_follow' && n.referenceId) return navigate(`/playlist/${n.referenceId}`);
    const videoId = n.preview?.videoId;
    if (videoId) navigate(`/v/${videoId}`);
  };

  return (
    <Page>
      <PageHeader title="Meldingen" fallback="/me" />
      <div className={styles.filters}>
        <Chips
          items={[
            { id: 'all', label: 'Alles' },
            { id: 'unread', label: 'Ongelezen' },
          ]}
          value={filter}
          onChange={setFilter}
        />
      </div>
      {list.initialLoading &&
        [0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.item}>
            <Skeleton w={44} h={44} r={22} />
            <Skeleton w="60%" h={14} />
          </div>
        ))}
      {!list.initialLoading && list.items.length === 0 && (
        <EmptyState icon="bell" title={filter === 'unread' ? 'Alles gelezen' : 'Nog geen meldingen'} text="Als iemand je volgt, je video leuk vindt of reageert, zie je dat hier." action={<Button variant="secondary" onClick={() => navigate('/discover')}>Ontdekken</Button>} />
      )}
      <ul className={styles.list}>
        {list.items.map((n) => (
          <li key={n.id} className={cx(styles.item, !n.read && !readLocally && styles.unread)}>
            <Link to={`/u/${n.actor.username}`} aria-label={n.actor.displayName}>
              <Avatar user={n.actor} size={44} />
            </Link>
            <button className={styles.text} onClick={() => open(n)}>
              <span>
                <strong>{n.actor.displayName}</strong> {TEXT[n.type]}
                {n.preview?.text && (n.type === 'comment' || n.type === 'comment_reply' || n.type === 'mention') && <span className={styles.quote}>: “{n.preview.text}”</span>}
                {n.type === 'sound_used' && n.preview?.text && <span className={styles.quote}> “{n.preview.text}”</span>}
                {n.type === 'playlist_follow' && n.preview?.text && <span className={styles.quote}> “{n.preview.text}”</span>}
              </span>
              <time dateTime={n.createdAt}>{timeAgo(n.createdAt)}</time>
            </button>
            {n.type === 'follow' ? (
              <FollowButton userId={n.actor.id} initial={n.followingActor ?? false} />
            ) : n.preview?.thumbnailUrl ? (
              <button onClick={() => open(n)} className={styles.thumb} aria-label="Video openen">
                <img src={n.preview.thumbnailUrl} alt="" loading="lazy" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <LoadMore onVisible={list.loadMore} loading={list.loading} hasMore={list.hasMore} />
    </Page>
  );
}
